import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import ChapterPartCard from './ChapterPartCard';
import Footer from './Footer';
import NavBar from './NavBar';
import Logo from './Logo';
import StatusLoader from './StatusLoader';
import {
  TinyText,
  TinyTitle,
  Container,
  Row,
  Column,
  Card,
  BaseLink
} from './CommonUiComponents';
import GoalsContainer from '../containers/GoalsContainer';
import { textGray } from '../utils/colors';
import Sheets from './Sheets';
import ErrorBoundary from './ErrorBoundary';
import hexToRgb from '../utils/hexToRGB';

const TopSection = styled.div`
  margin: 0 auto;
  position: relative;
  margin-top: 7rem;
  margin-bottom: 3.0375rem;
  padding-left: 12.5rem;

  @media all and (max-width: 1024px) {
    margin-top: 4rem;
    margin-bottom: 1rem;
    text-align: center;
    padding-left: 0;
  }
`;

const BookTitle = styled.h1`
  font-size: 4rem;
  line-height: 4.8125rem;
  color: ${textGray};
  margin-top: 0.75rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  @media all and (max-width: 1024px) {
    font-size: 2.5rem;
    line-height: 3rem;
  }
`;

const StyledPartWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  width: 100%;
  margin: 1.3125rem 0.9375rem;
  padding: 2rem 0;
  -webkit-overflow-scrolling: touch;
  position: relative;

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.quarternary};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.secondary};
    border-radius: 10px;
  }

  @media all and (max-width: 40rem) {
    padding: 1.5rem;
    margin: 0 -0.75rem;
    width: calc(100vw + 1.5rem);
  }
`;

const CardLink = BaseLink.extend`
  height: 255px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  width: 100%;
  font-size: 2rem;
  color: ${textGray};
  font-weight: 600;
  border-radius: 0.625rem;
  transition: background-color 0.12s ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const Shader = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 27px;
  box-shadow: inset -135px 0 50px -80px
    ${props => `rgba(${hexToRgb(props.theme.primary).toString(',')}, .3)`};
  width: 130px;
  z-index: 1;
`;

export default class Book extends Component {
  componentDidMount() {
    window.scroll(0, 0);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { navOpen, book } = this.props;
    if (navOpen !== nextProps.navOpen || book !== nextProps.book) return true;
    return false;
  }

  renderChapterParts() {
    const { book } = this.props.book;
    const { bookId } = this.props.match.params;

    if (!book) return null;

    return book.parts.map(item => {
      const totalReadingTime = item.chapters.reduce((reducedValue, element) => {
        return reducedValue + element.estimatedTime;
      }, 0);

      const userReadTime = item.chapters.reduce((reducedValue, element) => {
        return element.read
          ? reducedValue + element.estimatedTime
          : reducedValue;
      }, 0);

      const nextUnreadChapter = item.chapters.find(f => !f.read);

      const chapterToRead = nextUnreadChapter
        ? nextUnreadChapter.slug
        : item.chapters[0].slug;

      return (
        <ChapterPartCard
          bookId={bookId}
          to={`/bok/${bookId}/kapittel/${chapterToRead}`}
          key={item.key}
          gfx={item.gfx && item.gfx.sizes.medium_large}
          part={item.title}
          name={item.name}
          chapters={item.chapters}
          progress={Math.round((userReadTime / totalReadingTime) * 100)}
        />
      );
    });
  }

  render() {
    const {
      book: { book, fetchingBook },
      invertedTheme,
      navOpen
    } = this.props;

    return (
      <div>
        <NavBar primary>
          {book && (
            <BaseLink to={`/bok/${book.id}`}>
              <Logo size="4.1875rem" />
            </BaseLink>
          )}
          <TinyTitle inverted={invertedTheme && !navOpen}>
            {book && book.bookCategory}
          </TinyTitle>
          <TinyText italic inverted={invertedTheme && !navOpen}>
            «{book && book.title}»
          </TinyText>
        </NavBar>

        <Container>
          <Row>
            <Column>
              <TopSection>
                {book && (
                  <BookTitle inverted={invertedTheme}>{book.title}</BookTitle>
                )}
              </TopSection>
            </Column>
          </Row>
        </Container>

        <Container>
          <Row>
            {book && book.parts.length > 3 && <Shader />}
            <StyledPartWrapper>
              <ErrorBoundary>
                <StatusLoader busy={fetchingBook} center large>
                  {this.renderChapterParts()}
                </StatusLoader>
              </ErrorBoundary>
            </StyledPartWrapper>
          </Row>
        </Container>

        <Container>
          <Row>
            <Column>
              {book && <Sheets book={book.id} invertedTheme={invertedTheme} />}
            </Column>
            <Column>
              <GoalsContainer />
              {book && (
                <Card>
                  <CardLink
                    inverted={invertedTheme}
                    to={`/bok/${book.id}/min-side`}
                  >
                    Gå til min side
                  </CardLink>
                </Card>
              )}
            </Column>
          </Row>
        </Container>
        {book && <Footer inverted={invertedTheme} bookId={book.id} />}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Book.js